import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from 'Constants/env';
import { IContactApiPagination } from 'Interfaces/contactApiPagination';
import { ContactModelV2, IContact as IContactV2 } from 'Models/ContactModelV2';
import { DEFAULT_CONTACTS_LIMIT } from 'Modules/conversation/createConversation/components/createConversationForm/constants';
import { SearchableContactModelV2 } from 'Stores/SearchStore.types';
import API from '~/api';

// Query keys
export const QUERY_KEY_FETCH_OPT_OUT_STATUS = 'conversation.fetchOptOutStatus';
export const QUERY_KEY_FETCH_PERSON_BY_PERSON_ID = 'conversation.fetchPersonByPersonId';
export const QUERY_KEY_FETCH_CONVERSATIONS = 'conversation.fetchConversations';
export const QUERY_KEY_FETCH_CONVERSATION = 'conversation.fetchConversation';
export const QUERY_KEY_FETCH_CONTACTS = 'contacts.fetchContacts';

// Interfaces
interface FetchContactsParams {
  query?: string;
  limit?: number;
  nextUrl?: string;
}

interface PageParam {
  nextUrl?: string;
}

/*FIXME: For now we need to export this, so we can call on the mobx store (class instance)
 * We will also be able to rely on "useQueries" to parallelize the requests, instead of the Promise.allSettled
 */
export const fetchContactsWithPhoneNumber = async (
  phoneNumber: string
): Promise<ContactModelV2[]> => {
  const contacts = await API.get<{ data: Record<string, IContactV2[]> }>(
    API_ENDPOINTS.MatchContactByPhoneNumber(),
    { params: { phoneNumbers: [phoneNumber] } }
  ).then((response) => {
    // Just getting the key of the returned contact, in case it comes in a different format
    const phoneNumberKey = Object.keys(response.data.data)[0];

    if (!phoneNumberKey) {
      return [];
    }

    return response.data.data[phoneNumberKey].map((contact) =>
      ContactModelV2.fromResponseDto(contact)
    );
  });

  return contacts;
};

export const fetchContacts = async ({
  query = '',
  limit,
  nextUrl,
}: FetchContactsParams) => {
  const endpoint = nextUrl
    ? nextUrl.replace('http', 'https')
    : API_ENDPOINTS.FetchContactsV2({
        sourcePlatforms: ['Bhive', 'ContactAPI', 'Nylas'],
        searchTerm: query,
        orderBy: 'name',
        perPage: limit,
        page: 1,
      });

  const resp = await API.get<IContactApiPagination<IContactV2>>(endpoint);

  const contacts = resp.data.data.map(
    (contact) =>
      new SearchableContactModelV2(ContactModelV2.fromResponseDto(contact))
  );

  return {
    contacts,
    nextPageUrl: resp.data.links.next,
    hasMore: !!resp.data.links.next,
  };
};

export const fetchMoreContacts = async ({ nextUrl }: { nextUrl: string }) => {
  if (!nextUrl) {
    return {
      contacts: [],
      nextPageUrl: null,
      hasMore: false,
    };
  }

  const resp = await API.get<IContactApiPagination<IContactV2>>(
    nextUrl.replace('http', 'https')
  );

  const contacts = resp.data.data.map(
    (contact) =>
      new SearchableContactModelV2(ContactModelV2.fromResponseDto(contact))
  );

  return {
    contacts,
    nextPageUrl: resp.data.links.next,
    hasMore: !!resp.data.links.next,
  };
};

// Hooks
export const useFetchContactsWithPhoneNumber = (phoneNumber: string) =>
  useQuery({
    queryKey: [QUERY_KEY_FETCH_OPT_OUT_STATUS, phoneNumber],
    queryFn: async () => fetchContactsWithPhoneNumber(phoneNumber),
  });

export const useFetchContacts = (query: string, limit = DEFAULT_CONTACTS_LIMIT) => {
  return useQuery({
    queryKey: [QUERY_KEY_FETCH_CONTACTS, 'initial', query, limit],
    queryFn: () => fetchContacts({ query, limit }),
    enabled: true
  });
};

export const useInfiniteContacts = (query: string, limit = DEFAULT_CONTACTS_LIMIT) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY_FETCH_CONTACTS, 'infinite', query, limit],
    queryFn: ({ pageParam }: { pageParam: PageParam | undefined }) => {
      if (pageParam?.nextUrl) {
        return fetchMoreContacts({ nextUrl: pageParam.nextUrl });
      }
      return fetchContacts({ query, limit });
    },
    getNextPageParam: (lastPage: { hasMore: boolean; nextPageUrl: string; contacts: any[] }) => {
      if (!lastPage.hasMore) {
        return undefined;
      }
      return { nextUrl: lastPage.nextPageUrl };
    },
    initialPageParam: undefined as PageParam | undefined,
    enabled: true,
  });
};